import {
  Box,
  Table,
  Text,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  BoxProps,
  Icon,
  ButtonGroup,
  IconButton,
  useToast,
} from '@chakra-ui/react';
import axios from 'axios';
import { useCallback, useState } from 'react';
import { RiEyeLine, RiLoginCircleLine } from 'react-icons/ri';
import { ConfirmationModal } from '../../../../../../../components/ConfirmationModal';
import { LinkButton } from '../../../../../../../components/LinkButton';
import { Pagination } from '../../../../../../../components/Pagination';
import { TableFilters } from '../../../../../../../components/TableFilters';
import { useAuth } from '../../../../../../../hooks/auth';
import { PrettyBookingPaymentStatus } from '../../../../../../../models/bookings';
import { createBookingCheckinsService } from '../../../../../../../services/Bookings/CreateBookingCheckinsService';
import {
  IBookingListCheckin,
  IBookingListItem,
} from '../../../../../../../services/Bookings/ListBookingsService';
import { translateError } from '../../../../../../../utils/errors';
import { maskDateTime } from '../../../../../../../utils/formatters/handleMask';

export interface IBookingNoShowTableItemCheckin extends IBookingListCheckin {
  formattedCreatedAt: string;
}

export interface IBookingNoShowTableItem extends IBookingListItem {
  formattedBookedDate: string;
  formattedCreatedAt: string;
  formattedEndTime: string;
  formattedStartTime: string;
}

interface IBookingNoShowTableProps extends BoxProps {
  bookings: IBookingNoShowTableItem[];
  currentPage?: number;
  filterBy?: string;
  onChangeFilterBy?: (value: string) => void;
  onCheckin?: (bookingCheckin: IBookingNoShowTableItemCheckin) => void;
  onFilter?: (value: string) => void;
  onOrder?: (order: 'ASC' | 'DESC') => void;
  onPageChange?: (page: number) => void;
  onSort?: (sort: 'bookedDate' | 'createdAt') => void;
  order?: 'ASC' | 'DESC';
  sortBy?: string;
  totalPages?: number;
}

export const BookingNoShowTable = ({
  bookings,
  currentPage,
  filterBy,
  onChangeFilterBy,
  onCheckin,
  onFilter,
  onOrder,
  onPageChange,
  onSort,
  order,
  sortBy,
  totalPages,
  ...rest
}: IBookingNoShowTableProps): JSX.Element => {
  const filterByOptions = [
    {
      label: 'Nome',
      value: 'userName',
    },
    {
      label: 'Documento',
      value: 'userDocument',
    },
    {
      label: 'Nome da atividade',
      value: 'activityName',
    },
    {
      label: 'Nome do local',
      value: 'spotName',
    },
  ];

  const sortByOptions = [
    {
      label: 'Horário reservado',
      value: 'bookedDate',
    },
    {
      label: 'Reservado em',
      value: 'createdAt',
    },
  ];

  const { assignment: authenticatedAssignment } = useAuth();

  const toast = useToast();
  const [handlingBooking, setHandlingBooking] =
    useState<IBookingNoShowTableItem>();
  const [
    isCheckinConfirmationModalVisible,
    setIsCheckinConfirmationModalVisible,
  ] = useState(false);

  const handleTooggleCheckinConfirmationModal = useCallback(() => {
    setIsCheckinConfirmationModalVisible((prevState) => !prevState);
  }, []);

  const handleCreateCheckin = async (): Promise<void> => {
    if (handlingBooking && !!onCheckin) {
      try {
        const checkin = await createBookingCheckinsService(handlingBooking.id);

        handleTooggleCheckinConfirmationModal();

        onCheckin({
          ...checkin,
          formattedCreatedAt: maskDateTime(checkin.createdAt),
        });
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao cadastrar',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao cadastrar o checkin, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    }
  };

  return (
    <Box {...rest}>
      {handlingBooking && (
        <ConfirmationModal
          isOpen={isCheckinConfirmationModalVisible}
          onClose={handleTooggleCheckinConfirmationModal}
          onConfirm={handleCreateCheckin}
          title="Confirmar checkin"
          message="Deseja realmente criar um checkin para essa reserva? Ela será removida a lista de no-shows."
        />
      )}

      <TableFilters
        filterBy={filterBy}
        filterByOptions={filterByOptions}
        filterInputType={filterBy === 'bookedDate' ? 'datepicker' : 'input'}
        onChangeFilterBy={onChangeFilterBy}
        onFilter={onFilter}
        onOrder={onOrder}
        onSort={(sort) =>
          !!onSort && onSort(sort as 'bookedDate' | 'createdAt')
        }
        order={order}
        sortBy={sortBy}
        sortByOptions={sortByOptions}
      />

      <Table colorScheme="blue" mt="4">
        <Thead>
          <Tr>
            <Th>Membro</Th>

            <Th>Atividade / Local</Th>

            <Th textAlign="center">Horário reservado</Th>

            <Th textAlign="center">Reservado em</Th>

            <Th>Status pagamento</Th>

            <Th textAlign="right" w="8" />
          </Tr>
        </Thead>

        <Tbody fontSize="sm">
          {bookings.map((booking) => (
            <Tr key={booking.id}>
              <Td>{booking.user.name}</Td>

              <Td>
                <Box>
                  <Text>{booking.activitySchedule.activity.name}</Text>

                  <Text fontSize="xs" color="gray.500">
                    {booking.activitySchedule.activity.spot.name}
                  </Text>
                </Box>
              </Td>

              <Td textAlign="center">
                <Box>
                  <Text>{booking.formattedBookedDate}</Text>
                  <Text>{`${booking.formattedStartTime} - ${booking.formattedEndTime}`}</Text>
                </Box>
              </Td>

              <Td textAlign="center">
                <Text>{booking.formattedCreatedAt.split('-')[0]}</Text>
                <Text>{booking.formattedCreatedAt.split('-')[1]}</Text>
              </Td>

              <Td>
                {booking.payment
                  ? PrettyBookingPaymentStatus[booking.payment.status]
                  : 'Isento'}
              </Td>

              <Td textAlign="right">
                <ButtonGroup>
                  {!booking.deletedAt &&
                    !!onCheckin &&
                    (!booking.payment ||
                      [
                        'APPROVED',
                        'PAYMENT_EXTERNAL_PENDING',
                        'PAYMENT_EXTERNAL',
                      ].includes(booking.payment.status)) &&
                    authenticatedAssignment?.featureGroup.features.some(
                      (feature) =>
                        [
                          'BOOKING_CHECKIN_FULL_ACCESS',
                          'BOOKING_CHECKIN_READ_ACCESS',
                          'BOOKING_CHECKIN_WRITE_ACCESS',
                        ].includes(feature.key),
                    ) && (
                      <IconButton
                        aria-label="Checkins"
                        size="sm"
                        icon={<Icon as={RiLoginCircleLine} fontSize="16" />}
                        fontSize="16"
                        colorScheme="green"
                        onClick={() => {
                          setHandlingBooking(booking);
                          handleTooggleCheckinConfirmationModal();
                        }}
                      />
                    )}

                  <LinkButton
                    px={0}
                    to={{
                      pathname: '/bookings/details',
                      state: {
                        bookingId: booking.id,
                      },
                    }}
                  >
                    <Icon as={RiEyeLine} fontSize="16" />
                  </LinkButton>
                </ButtonGroup>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {!!currentPage && !!onPageChange && !!totalPages && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          onPageChange={onPageChange}
          totalPages={totalPages}
        />
      )}
    </Box>
  );
};
